import React, { useState, useEffect } from "react";
import "./App.css";
import { API, graphqlOperation, Hub } from "aws-amplify";
import Auth from "@aws-amplify/auth";
import { DateTime } from "luxon";
import * as mutations from "./graphql/mutations";
import SimpleTabs from "./components/SimpleTabs";
import Footer from "./components/Footer";
import Header from "./components/Header";
import Login from "./Login/Login";
import { getAllEvents } from "./utils/api";
import * as subscriptions from "./graphql/subscriptions";
import {
  BrowserRouter as Router,
  Switch,
  Route,
  Redirect,
} from "react-router-dom";

function App() {
  const [created, setCreated] = useState(false);
  const [deleted, setDeleted] = useState(false);
  const [wait, setWait] = useState(false);
  const [notPossible, setNotPossible] = useState(false);
  const [events, setEvents] = useState<Array<any>>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isSubscribed, setIsSubscribed] = useState(true);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isAdmin, setIsAdmin] = useState(false);
  const [isLoadingAuth, setIsLoadingAuth] = useState(true);

  const checkDuplicates = async (info: any, arena_number: Number) => {
    return getAllEvents(setIsAuthenticated).then(async (response) => {
      let duplicates = response.find(
        (event) =>
          event.arena_number === arena_number &&
          event.start === info.date.toISOString()
      );
      if (duplicates && !isAdmin) {
        return true;
      } else {
        return false;
      }
    });
  };
  const eventMutation = async (info: any, arena_number: Number) => {
    // Can not create events that exist, are in the past or on the current day
    return checkDuplicates(info, arena_number).then(async (exists) => {
      const fromDayUTC = DateTime.fromHTTP(info.date.toUTCString(), {
        zone: "UTC",
      }).toISODate();
      const nowDay = DateTime.fromMillis(Date.now()).toISODate();

      if (!exists && info.date > Date.now() && nowDay !== fromDayUTC) {
        const tokens = await Auth.currentSession();
        const userName = tokens.getIdToken().payload["cognito:username"];
        const userEmail = tokens.getIdToken().payload["email"];
        const to = DateTime.fromJSDate(info.date)
          .plus({ minutes: 30 })
          .toJSDate();
        const eventDetails = {
          user_id: userName,
          user_email: userEmail,
          valid_from: info.date,
          valid_to: to,
          arena_number,
        };
        await API.graphql(
          graphqlOperation(mutations.createEvent, { input: eventDetails })
        );
        return true;
      }
      return false;
    });
  };
  const eventDelete = async (info: any) => {
    const eventDetails = {
      id: info.event.id,
    };
    // Can not remove past events
    if (info.event.start > Date.now()) {
      await API.graphql(
        graphqlOperation(mutations.deleteEvent, { input: eventDetails })
      );
      getAllEvents(setIsAuthenticated).then((response) => {
        setEvents(response);
        setIsLoading(false);
      });
      return true;
    }
    return false;
  };
  const subscribeUpdateChanges = async () => {
    let unsubscribe;
    let subscription: any = await API.graphql(
      graphqlOperation(subscriptions.onCreateEvent)
    );
    subscription.subscribe({
      next: (payload: any) => {
        getAllEvents(setIsAuthenticated).then((response) => {
          setEvents(response);
          setIsLoading(false);
        });
      },
    });
    unsubscribe = () => {
      subscription.unsubscribe();
    };
    return unsubscribe;
  };
  const subscribeDeleteChanges = async () => {
    let subscription: any = await API.graphql(
      graphqlOperation(subscriptions.onDeleteEvent)
    );
    subscription.subscribe({
      next: (payload: any) => {
        getAllEvents(setIsAuthenticated).then((response) => {
          setEvents(response);
          setIsLoading(false);
        });
      },
    });
  };

  const checkAuth = async () => {
    try {
      let tokens = await Auth.currentSession();
      return tokens;
    } catch (error) {
      console.error("Unable to load session");
      throw error;
    }
  };

  useEffect(() => {
    Hub.listen("auth", (data) => {
      if (data.payload.event === "signIn") {
        getAllEvents(setIsAuthenticated).then((response) => {
          setEvents(response);
          setIsLoading(false);
        });
      }
    });
    if (isLoading) {
      getAllEvents(setIsAuthenticated).then((response) => {
        setEvents(response);
        setIsLoading(false);
      });
    }

    if (isSubscribed && isAuthenticated) {
      subscribeUpdateChanges();
      subscribeDeleteChanges();
      setIsSubscribed(false);
    }
    if (isLoadingAuth) {
      checkAuth()
        .then((resp) => {
          setIsLoadingAuth(false);
          setIsAuthenticated(true);
          if (resp.getAccessToken().payload["cognito:groups"]) {
            setIsAdmin(
              resp.getAccessToken().payload["cognito:groups"].includes("Admins")
            );
          }
        })
        .catch((e) => {
          setIsAuthenticated(false);
        });
    }
  }, [
    events,
    isAdmin,
    isAuthenticated,
    isLoading,
    isLoadingAuth,
    isSubscribed,
    setEvents,
  ]);

  const dateClick = (info: any, arena_number: Number) => {
    setWait(true);
    eventMutation(info, arena_number).then(async (created) => {
      if (created) {
        setCreated(true);
        setWait(false);
        setTimeout(() => {
          setCreated(false);
        }, 4000);
      } else {
        setCreated(false);
        setWait(false);
        setNotPossible(true);
        setTimeout(() => {
          setNotPossible(false);
        }, 4000);
      }
    });
  };

  const eventClick = (info: any) => {
    setWait(true);
    eventDelete(info).then((wasDeleted) => {
      if (wasDeleted) {
        setDeleted(true);
        setWait(false);
        setTimeout(() => {
          setDeleted(false);
        }, 4000);
      } else {
        setWait(false);
        setNotPossible(true);
        setTimeout(() => {
          setNotPossible(false);
        }, 4000);
      }
    });
  };

  return (
    <Router>
      <Switch>
        <Route path="/login">
          <Login />
        </Route>
        <Route exact path="/kentta/:arena_number?">
          <div className="App">
            <Header
              wait={wait}
              created={created}
              deleted={deleted}
              notPossible={notPossible}
            />
            <SimpleTabs
              events={events}
              dateClick={dateClick}
              eventClick={eventClick}
              isAdmin={isAdmin}
            ></SimpleTabs>
            <Footer />
          </div>
        </Route>
        <Route path="/">
          {!isAuthenticated ? (
            <Redirect to="/login" />
          ) : (
            <Redirect to="/kentta/1" />
          )}
        </Route>
      </Switch>
    </Router>
  );
}

export default App;
